// buildTimelineGetters — the LIVE-STATUS presentation getters shared by the
// wizard's full-screen generation completion checklist
// (wizard-generation-progress.ts) and the CMS/management build-status pill
// (website-build-status.ts). Both bind the SAME server-rendered rows (see
// templates/components/build_timeline.templ's BuildStageTimeline) to live
// status via these getters, keyed by stage INDEX.
//
// Extracted 2026-07-12 (post-parallelization follow-ups Part 1, Task 1.4).
// LIVE-STATUS getters only — `failedStageLabel` (and its dependency on a
// server-precomputed `stageLabels` array) stays wizard-only: it drives the
// wizard's reactive failure-line text, which the pill has no equivalent of
// (its popover has no per-stage failure line, just a coarse "publish failed"
// message).
//
// Design note: these are spread directly onto the Alpine data object (e.g.
// `...buildTimelineGetters()` alongside `stages: [...]`) rather than closing
// over a `getState()` accessor. Alpine's `Alpine.data(name, factory)`
// registration binds the FACTORY function's own `this` to an internal magics
// context (NOT the object the factory returns — see alpinejs/src/datas.js
// `injectDataProviders`/`callback.bind(context)`), so an arrow function
// created inline inside the factory's return statement would capture the
// WRONG `this` and silently break (`this.stages` resolving to `undefined`).
// Plain methods spread onto the final data object don't have this problem:
// when Alpine (or any caller) invokes `component.stAt(i)`, ordinary
// property-access-then-call semantics bind `this` to `component`, which is
// exactly the object that owns `stages`/`phase` — the same mechanism the
// pre-extraction inline getters relied on.

export interface BuildStage {
  key: string;
  label_args?: Record<string, string>;
  status: string;
  progress?: { done: number; total: number };
  // pass counts how many times this stage has been (re-)activated: 1 on
  // first activation, 2+ on a genuine re-open (the area enrichment
  // pipeline's reground loop). Absent/0 on every website-build stage —
  // buildRunTracker never sets it — so a "repeat pass" UI must key off
  // pass > 1, never pass !== 1 (see stRepeat below and
  // domain.BuildStage.Pass's Go-side doc comment).
  pass?: number;
}

// BuildTimelineHost is the shape a host Alpine component must expose for
// these getters to work once spread onto it (both wizardGenerationProgress
// and websiteBuildStatus satisfy this).
export interface BuildTimelineHost {
  stages: BuildStage[];
  unitLabel(s: BuildStage): string;
}

export function buildTimelineGetters() {
  return {
    // stAt returns the live status of the stage at index i (pending when
    // the snapshot is shorter than the seeded checklist).
    stAt(this: BuildTimelineHost, i: number): string {
      const s = this.stages[i];
      return s ? s.status : "pending";
    },

    // stProgress returns a "done/total" string for the stage at index i
    // when it carries live sub-progress AND is still active (the
    // analyze_images image-analysis counter), else ''. Clears itself
    // once the stage settles (status flips off 'active'), so the count
    // disappears when the check lands.
    stProgress(this: BuildTimelineHost, i: number): string {
      const s = this.stages[i];
      if (!s || s.status !== "active" || !s.progress || !s.progress.total) return "";
      return `${s.progress.done}/${s.progress.total}`;
    },

    // stRepeat reports whether the stage at index i is a genuine re-open
    // (pass > 1) rather than a first activation (pass === 1, or absent/0 on
    // every stage that predates this field). Currently only meaningful for
    // the area enrichment pipeline's timeline (AreaStageTimeline /
    // area-timeline.ts) — website-build stages never re-open, so this is
    // always false there, but it lives here (not duplicated in
    // area-timeline.ts) since it reads the same `stages[i]` shape every
    // other getter here does.
    stRepeat(this: BuildTimelineHost, i: number): boolean {
      const s = this.stages[i];
      return !!s && (s.pass || 0) > 1;
    },

    // grpDone counts the settled (done + skipped) members of a group.
    grpDone(this: BuildTimelineHost, idxs: number[]): number {
      return idxs.filter((i) => {
        const s = this.stages[i];
        return !!s && (s.status === "done" || s.status === "skipped");
      }).length;
    },

    // grpStatus aggregates a group into one icon state: failed if any
    // member failed, active if any is running, done once all are
    // settled, else pending.
    grpStatus(this: BuildTimelineHost, idxs: number[]): string {
      let anyActive = false;
      let allDone = true;
      for (const i of idxs) {
        const s = this.stages[i];
        if (!s) {
          allDone = false;
          continue;
        }
        if (s.status === "failed") return "failed";
        if (s.status === "active") anyActive = true;
        if (s.status !== "done" && s.status !== "skipped") allDone = false;
      }
      if (anyActive) return "active";
      return allDone ? "done" : "pending";
    },

    // grpActiveLabel is the label of the currently-active member, shown
    // next to the collapsed group's count.
    grpActiveLabel(this: BuildTimelineHost, idxs: number[]): string {
      for (const i of idxs) {
        const s = this.stages[i];
        if (s && s.status === "active") return this.unitLabel(s);
      }
      return "";
    },

    // unitLabel renders a stage's concrete unit from its label_args —
    // "City Loft (EN)" for a per-property translation, the bare
    // name/locale otherwise. Proper nouns / locale codes, so no i18n.
    unitLabel(s: BuildStage): string {
      const a = s.label_args || {};
      if (a.name && a.locale) return `${a.name} (${a.locale.toUpperCase()})`;
      if (a.name) return a.name;
      if (a.locale) return a.locale.toUpperCase();
      return "";
    },
  };
}
